import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { API_BASE_URL, fetchJson, tryPaths } from './api';
import {
    AnalyticsMetricCard,
    AnalyticsPageHero,
    AnalyticsPanelSection,
    analyticsPanelSx,
    glassSubCardSx
} from './analyticsSurface';
import RecommendedSurveyorsPanel from './RecommendedSurveyorsPanel';

function parseJobReference(refLike) {
    const raw = String(refLike || '').trim();
    if (!raw) return { raw: '', prefix: '', num: null };
    const m = raw.match(/^([a-z]+)\s*0*(\d+)$/i);
    if (!m) return { raw, prefix: raw.toUpperCase(), num: null };
    return { raw, prefix: String(m[1]).toUpperCase(), num: Number(m[2]) };
}

function compareJobReferences(aRef, bRef) {
    const a = parseJobReference(aRef);
    const b = parseJobReference(bRef);
    if (a.prefix !== b.prefix) return a.prefix.localeCompare(b.prefix);
    if (a.num !== null && b.num !== null && a.num !== b.num) return a.num - b.num;
    return a.raw.localeCompare(b.raw, undefined, { numeric: true, sensitivity: 'base' });
}

function formatMoney(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: num % 1 === 0 ? 0 : 2
    }).format(num);
}

function toUkIsoDateOnly(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    const date = value instanceof Date ? value : new Date(raw);
    if (!Number.isFinite(date.getTime())) return '';

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    return year && month && day ? `${year}-${month}-${day}` : '';
}

function normalizeProjectDates(project) {
    const next = project && typeof project === 'object' ? { ...project } : project;
    if (!next || typeof next !== 'object') return next;

    ['instruction_date', 'final_report_issue_date', 'status_updated_at', 'questionnaire_sent_at', 'portal_updated_at'].forEach((key) => {
        if (next[key] == null || next[key] === '') return;
        const iso = toUkIsoDateOnly(next[key]);
        if (iso) next[key] = iso;
    });

    return next;
}

function formatDisplayDate(value) {
    const raw = toUkIsoDateOnly(value) || String(value || '').trim();
    if (!raw) return '—';
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s].*$)/);
    if (!match) return raw;

    return `${match[3]}/${match[2]}/${match[1]}`;
}

function normalizeFeeAmount(value) {
    const numeric = Number(String(value ?? '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(numeric) ? String(numeric) : '';
}

function firstAddressLine(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const firstLine = raw.split(/\r?\n/).map((part) => String(part || '').trim()).find(Boolean) || '';
    return String(firstLine.split(',')[0] || '').trim() || raw;
}

function buildInstructionFormFromFeeProposal(proposal) {
    const row = proposal && typeof proposal === 'object' ? proposal : {};
    const instructionDate = toUkIsoDateOnly(row.issued_at || row.issue_date || new Date());
    const projectName = firstAddressLine(row.project_address || row.client_name || '');
    return {
        project_name: projectName,
        reference: '',
        site_address: String(row.project_address || '').trim(),
        borrower_company: '',
        instruction_date: instructionDate,
        initial_report_fee: normalizeFeeAmount(row.proposed_initial_report_fee),
        interim_report_fee: normalizeFeeAmount(row.proposed_interim_report_fee),
        funder: String(row.client_name || '').trim(),
        funder_poc: String(row.client_point_of_contact || '').trim()
    };
}

function formatProgressIndicator(reportType, interimNumber) {
    const type = String(reportType || '').trim().toLowerCase();
    if (type === 'interim') {
        const n = String(interimNumber || '').trim();
        return n ? `Interim ${n}` : 'Interim';
    }
    if (type === 'initial') return 'Initial';
    return '';
}

function getTenantSlugFromStoredAuthToken() {
    const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
    if (!token) return 'iesis';
    const parts = token.split('.');
    if (parts.length < 2) return 'iesis';

    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const payload = JSON.parse(atob(padded));
        return String(payload?.tenant_slug || '').trim().toLowerCase() || 'iesis';
    } catch {
        return 'iesis';
    }
}

function ProjectSetup() {
    const tenantSlug = useMemo(() => getTenantSlugFromStoredAuthToken(), []);
    const completedStorageKey = useMemo(() => `monsurv_completed_projects:${tenantSlug}`, [tenantSlug]);
    const instructionProgressMetaStorageKey = useMemo(() => `monsurv_instruction_progress_meta:${tenantSlug}`, [tenantSlug]);

    // Load clients and team members (localStorage fallback + API hydrate)
    const [clients, setClients] = useState([]);
    const [team, setTeam] = useState([]);
    useEffect(() => {
        try {
            const rawClients = localStorage.getItem('monsurv_clients');
            setClients(rawClients ? JSON.parse(rawClients) : []);
        } catch (e) { setClients([]); }
        try {
            const rawTeam = localStorage.getItem('monsurv_team');
            setTeam(rawTeam ? JSON.parse(rawTeam) : []);
        } catch (e) { setTeam([]); }
    }, []);

    useEffect(() => {
        let cancelled = false;

        function normalizeTeamMember(m) {
            if (!m || typeof m !== 'object') return null;
            const role = String(m.role ?? m.access_role ?? '').trim().toLowerCase() || 'standard';
            const name = String(m.name ?? m.full_name ?? '').trim();
            const id = m.id ?? m.member_id ?? m.team_member_id ?? m.user_id ?? m.user_uuid ?? m.email ?? name;
            if (!name) return null;
            return {
                id,
                name,
                position: String(m.position ?? '').trim(),
                email: String(m.email ?? '').trim(),
                role
            };
        }

        async function hydrateTeamFromApi() {
            // Same endpoints as Team page; ProjectSetup only needs names/roles.
            const TEAM_API_PATHS = ['/team', '/api/team', '/team_members', '/api/team_members'];
            try {
                const list = await tryPaths(
                    TEAM_API_PATHS,
                    (path) => `${API_BASE_URL}${path}`,
                    { method: 'GET', headers: { ...authHeaders() } }
                );
                const raw = Array.isArray(list?.data) ? list.data : (Array.isArray(list) ? list : []);
                const normalized = raw.map(normalizeTeamMember).filter(Boolean);
                if (!cancelled && normalized.length) setTeam(normalized);
            } catch {
                // ignore: localStorage fallback is fine
            }
        }

        function onTeamUpdated(e) {
            const next = e?.detail;
            if (Array.isArray(next)) setTeam(next);
        }

        void hydrateTeamFromApi();
        window.addEventListener('monsurv:team-updated', onTeamUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener('monsurv:team-updated', onTeamUpdated);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [projects, setProjects] = useState([]);
    const [form, setForm] = useState({
        project_name: '',
        reference: '',
        site_address: '',
        borrower_company: '',
        instruction_date: '',
        initial_report_fee: '',
        interim_report_fee: '',
        funder: '',
        funder_poc: '',
        last_issued_report_type: '',
        interim_report_number: ''
    });
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetRef, setDeleteTargetRef] = useState('');
    const [deleteTargetIsCompleted, setDeleteTargetIsCompleted] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [finalReportDialogOpen, setFinalReportDialogOpen] = useState(false);
    const [finalReportTargetProject, setFinalReportTargetProject] = useState(null);
    const [finalReportIssueDate, setFinalReportIssueDate] = useState('');
    const [finalReportBusy, setFinalReportBusy] = useState(false);
    const [finalReportError, setFinalReportError] = useState('');
    const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
    const [updateStatusTargetProject, setUpdateStatusTargetProject] = useState(null);
    const [updateStatusBusy, setUpdateStatusBusy] = useState(false);
    const [updateStatusError, setUpdateStatusError] = useState('');
    const [expandedRef, setExpandedRef] = useState(null);
    const [existingSearch, setExistingSearch] = useState('');
    const [durationByRef, setDurationByRef] = useState({});
    const [durationLoadingByRef, setDurationLoadingByRef] = useState({});

    const [completedByRef, setCompletedByRef] = useState(() => {
        try {
            const raw = localStorage.getItem(completedStorageKey);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(completedStorageKey, JSON.stringify(completedByRef));
        } catch (e) { }
    }, [completedByRef, completedStorageKey]);

    const hasArchiveEntry = useCallback((reference) => {
        const ref = String(reference || '').trim();
        if (!ref) return false;
        return Object.prototype.hasOwnProperty.call(completedByRef, ref);
    }, [completedByRef]);

    function getArchiveStatus(reference, projectStatus) {
        const ref = String(reference || '').trim();
        const archivedValue = String(completedByRef?.[ref] || '').trim().toLowerCase();
        const status = String(projectStatus || '').trim().toLowerCase();
        if (archivedValue === 'defunct' || status === 'defunct') return 'defunct';
        return 'completed';
    }

    const [showProjectForm, setShowProjectForm] = useState(false);
    const [instructionFile, setInstructionFile] = useState(null);
    const [instructionFileError, setInstructionFileError] = useState('');
    const [instructionUploading, setInstructionUploading] = useState(false);
    const [inProgressReportFile, setInProgressReportFile] = useState(null);
    const [inProgressReportFileError, setInProgressReportFileError] = useState('');
    const [issuedFeeProposals, setIssuedFeeProposals] = useState([]);
    const [issuedFeeProposalsLoading, setIssuedFeeProposalsLoading] = useState(false);
    const [issuedFeeProposalsError, setIssuedFeeProposalsError] = useState('');
    const [showInstructionFormFields, setShowInstructionFormFields] = useState(false);
    const [instructionFormMode, setInstructionFormMode] = useState('standard');
    const [selectedFeeProposal, setSelectedFeeProposal] = useState(null);

    const [instructionProgressMetaByRef, setInstructionProgressMetaByRef] = useState(() => {
        try {
            const raw = localStorage.getItem(instructionProgressMetaStorageKey);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(instructionProgressMetaStorageKey, JSON.stringify(instructionProgressMetaByRef));
        } catch {
            // ignore persistence failures
        }
    }, [instructionProgressMetaByRef, instructionProgressMetaStorageKey]);

    useEffect(() => {
        fetchJson(`${API_BASE_URL}/key_project_data?include_archived=1`, {
            method: 'GET'
        })
            .then(data => {
                const normalized = Array.isArray(data) ? data.map(normalizeProjectDates) : [];
                setProjects(normalized);
                if (normalized.length) {
                    const nextCompleted = {};
                    normalized.forEach((p) => {
                        const ref = String(p?.reference || '').trim();
                        if (!ref) return;
                        if (p?.final_report_issue_date) {
                            nextCompleted[ref] = String(p.final_report_issue_date).slice(0, 10);
                        } else {
                            const status = String(p?.status || '').toLowerCase();
                            if (status === 'completed') {
                                nextCompleted[ref] = nextCompleted[ref] || '';
                            }
                            if (status === 'defunct') {
                                nextCompleted[ref] = 'Defunct';
                            }
                        }
                    });
                    if (Object.keys(nextCompleted).length) {
                        setCompletedByRef((prev) => ({ ...prev, ...nextCompleted }));
                    }
                }
            })
            .catch((e) => {
                // Keep the page usable even if the endpoint is protected/unavailable.
                console.warn('Failed to load projects:', e?.message || e);
            });
    }, []);

    function getContactsForFunder(funderName) {
        const name = String(funderName || '').trim();
        if (!name) return [];
        const match = clients.find((c) => String(c?.companyName || c?.name || '').trim() === name);
        return Array.isArray(match?.contacts) ? match.contacts : [];
    }

    const existingProjects = useMemo(() => {
        const q = String(existingSearch || '').trim().toLowerCase();
        const base = projects.filter((p) => !hasArchiveEntry(p.reference));
        const filtered = !q ? base : base.filter((p) => {
            const hay = [
                p.reference,
                p.project_name,
                p.site_address,
                p.funder,
                p.lead_surveyor
            ].map(v => String(v || '').toLowerCase()).join(' ');
            return hay.includes(q);
        });
        const copy = filtered.slice();
        copy.sort((a, b) => compareJobReferences(a?.reference, b?.reference));
        return copy;
    }, [projects, existingSearch, hasArchiveEntry]);

    const completedProjects = useMemo(
        () => projects.filter((p) => hasArchiveEntry(p.reference)).sort((a, b) => compareJobReferences(a?.reference, b?.reference)),
        [projects, hasArchiveEntry]
    );

    const assignableSurveyors = useMemo(
        () => (Array.isArray(team) ? team : []).filter((member) => {
            const role = String(member?.role || '').toLowerCase();
            const position = String(member?.position || '').toLowerCase();
            if (role) return role === 'surveyor' || role === 'admin';
            return position.includes('surveyor') || position.includes('admin');
        }),
        [team]
    );

    const activeUnassignedCount = useMemo(
        () => existingProjects.filter((project) => !String(project?.lead_surveyor || '').trim()).length,
        [existingProjects]
    );

    const activeFeeValue = useMemo(
        () => existingProjects.reduce((sum, project) => {
            const initial = Number(project?.initial_report_fee) || 0;
            const interim = Number(project?.interim_report_fee) || 0;
            return sum + initial + interim;
        }, 0),
        [existingProjects]
    );

    const PROJECT_API_PATHS = useMemo(
        () => ['/key_project_data', '/api/key_project_data'],
        []
    );

    function computeDurationMonths(programmeRow) {
        const durationMonthsRaw = programmeRow?.duration_months;
        const durationWeeksRaw = programmeRow?.duration_weeks;

        const hasMonths = durationMonthsRaw !== null && durationMonthsRaw !== undefined && String(durationMonthsRaw) !== '';
        if (hasMonths) {
            const n = Number(durationMonthsRaw);
            return Number.isFinite(n) ? n : null;
        }

        const hasWeeks = durationWeeksRaw !== null && durationWeeksRaw !== undefined && String(durationWeeksRaw) !== '';
        if (hasWeeks) {
            const w = Number(durationWeeksRaw);
            if (!Number.isFinite(w)) return null;
            return Math.round(w / 4.345);
        }

        return null;
    }

    async function loadDurationFromInitialQuestionnaire(reference) {
        const ref = String(reference || '').trim();
        if (!ref) return null;
        if (durationByRef[ref] !== undefined) return durationByRef[ref];
        if (durationLoadingByRef[ref]) return null;

        setDurationLoadingByRef((prev) => ({ ...prev, [ref]: true }));
        try {
            const resp = await fetch(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/initial-report?mode=surveyor`, {
                method: 'GET',
                headers: authHeaders()
            });
            if (!resp.ok) throw new Error('Failed');
            const json = await resp.json();
            const programmeRow = json?.tables?.ini_section_8_programme?.[0] || null;
            const duration = computeDurationMonths(programmeRow);
            setDurationByRef((prev) => ({ ...prev, [ref]: duration }));
            return duration;
        } catch {
            setDurationByRef((prev) => ({ ...prev, [ref]: null }));
            return null;
        } finally {
            setDurationLoadingByRef((prev) => ({ ...prev, [ref]: false }));
        }
    }

    useEffect(() => {
        if (!expandedRef) return;
        void loadDurationFromInitialQuestionnaire(expandedRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedRef]);

    function getDurationDisplay(project) {
        const ref = String(project?.reference || '').trim();
        const derived = ref ? durationByRef[ref] : null;
        const fallback = project?.project_duration ?? null;
        const value = derived ?? fallback;
        if (value === null || value === undefined || value === '') return '—';
        return value;
    }

    async function deleteProjectByReference(reference, deletionPassword) {
        return tryPaths(
            PROJECT_API_PATHS,
            (path) => `${API_BASE_URL}${path}/reference/${encodeURIComponent(reference)}`,
            {
                method: 'DELETE',
                headers: {
                    ...authHeaders(),
                    ...(deletionPassword ? { 'X-Deletion-Password': String(deletionPassword) } : {})
                }
            }
        );
    }

    function openDeleteDialog(reference, { isCompleted } = {}) {
        const ref = String(reference || '').trim();
        if (!ref) return;
        setDeleteError('');
        setDeletePassword('');
        setDeleteTargetRef(ref);
        setDeleteTargetIsCompleted(Boolean(isCompleted));
        setDeleteDialogOpen(true);
    }

    function closeDeleteDialog() {
        if (deleteBusy) return;
        setDeleteDialogOpen(false);
        setDeleteError('');
        setDeletePassword('');
        setDeleteTargetRef('');
        setDeleteTargetIsCompleted(false);
    }

    async function confirmDeleteProject() {
        const ref = String(deleteTargetRef || '').trim();
        const pw = String(deletePassword || '').trim();
        if (!ref) return;
        if (!pw) {
            setDeleteError('Deletion password is required.');
            return;
        }

        setDeleteError('');
        setDeleteBusy(true);
        try {
            await deleteProjectByReference(ref, pw);
            setProjects((list) => (Array.isArray(list) ? list.filter((p) => p.reference !== ref) : list));
            setInstructionProgressMetaByRef((prev) => {
                const next = { ...(prev || {}) };
                delete next[ref];
                return next;
            });
            if (deleteTargetIsCompleted) {
                setCompletedByRef((prev) => {
                    const next = { ...(prev || {}) };
                    delete next[ref];
                    return next;
                });
            }
            setDeleteDialogOpen(false);
            setDeletePassword('');
            setDeleteTargetRef('');
            setDeleteTargetIsCompleted(false);
        } catch (e) {
            setDeleteError(String(e?.message || 'Error deleting project'));
        } finally {
            setDeleteBusy(false);
        }
    }

    async function updateProject(project, payload) {
        const ref = project.reference;
        const id = project.id;
        const candidates = [];

        // Prefer reference-based route (matches existing DELETE usage)
        candidates.push(...PROJECT_API_PATHS.map(p => `${p}/reference/${encodeURIComponent(ref)}`));
        // Also try id-based route if backend supports it
        if (id !== undefined && id !== null && id !== '') {
            candidates.push(...PROJECT_API_PATHS.map(p => `${p}/${encodeURIComponent(id)}`));
        }

        // tryPaths expects base paths; we can reuse it by creating a synthetic list
        let lastErr;
        for (const rel of candidates) {
            try {
                const res = await fetch(`${API_BASE_URL}${rel}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...authHeaders() },
                    body: JSON.stringify(payload)
                });
                const json = await res.json().catch(() => null);
                if (!res.ok) {
                    if (res.status === 404) { lastErr = new Error('Not found'); continue; }
                    throw new Error((json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`);
                }
                return json;
            } catch (e) {
                if (String(e?.message || '').trim() === 'Not found') {
                    lastErr = e;
                    continue;
                }
                lastErr = e;
                throw lastErr;
            }
        }
        throw lastErr || new Error('Update failed');
    }

    function validateFinalReportIssueDate(value) {
        const trimmed = String(value || '').trim();
        if (!trimmed) return null;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
        const dt = new Date(`${trimmed}T00:00:00`);
        if (Number.isNaN(dt.getTime())) return null;
        return trimmed;
    }

    function openFinalReportDialog(project, defaultValue) {
        setFinalReportTargetProject(project || null);
        setFinalReportIssueDate(String(defaultValue || '').trim());
        setFinalReportError('');
        setFinalReportDialogOpen(true);
    }

    function closeFinalReportDialog() {
        if (finalReportBusy) return;
        setFinalReportDialogOpen(false);
        setFinalReportTargetProject(null);
        setFinalReportIssueDate('');
        setFinalReportError('');
    }

    function openUpdateStatusDialog(project) {
        setUpdateStatusTargetProject(project || null);
        setUpdateStatusError('');
        setUpdateStatusBusy(false);
        setUpdateStatusDialogOpen(true);
    }

    function closeUpdateStatusDialog() {
        if (updateStatusBusy) return;
        setUpdateStatusDialogOpen(false);
        setUpdateStatusTargetProject(null);
        setUpdateStatusError('');
    }

    async function confirmFinalReportDialog() {
        const project = finalReportTargetProject;
        const ref = String(project?.reference || '').trim();
        if (!ref) return;

        const validated = validateFinalReportIssueDate(finalReportIssueDate);
        if (!validated) {
            setFinalReportError('Please enter a valid date in the format YYYY-MM-DD.');
            return;
        }

        setFinalReportBusy(true);
        setFinalReportError('');
        try {
            await updateProject(project, { ...project, final_report_issue_date: validated, status: 'completed' });

            setCompletedByRef(prev => ({ ...prev, [ref]: validated }));
            setExpandedRef(null);

            setFinalReportDialogOpen(false);
            setFinalReportTargetProject(null);
            setFinalReportIssueDate('');
            setMessage('Project marked as completed');
            setTimeout(() => setMessage(''), 2500);
        } catch (e) {
            setFinalReportError(e?.message || 'Failed to update final report issue date');
        } finally {
            setFinalReportBusy(false);
        }
    }

    function getTodayIsoDate() {
        const today = new Date();
        const yyyy = String(today.getFullYear());
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    function startCompleteProjectFlow() {
        const project = updateStatusTargetProject;
        if (!project) return;
        const defaultDate = getTodayIsoDate();
        closeUpdateStatusDialog();
        openFinalReportDialog(project, defaultDate);
    }

    async function markProjectDefunct() {
        const project = updateStatusTargetProject;
        const ref = String(project?.reference || '').trim();
        if (!ref) return;

        setUpdateStatusBusy(true);
        setUpdateStatusError('');
        try {
            await updateProject(project, { ...project, status: 'defunct', final_report_issue_date: null });
            setCompletedByRef((prev) => ({ ...prev, [ref]: 'Defunct' }));
            setExpandedRef(null);
            closeUpdateStatusDialog();
            setMessage('Project marked as defunct');
            setTimeout(() => setMessage(''), 2500);
        } catch (e) {
            setUpdateStatusError(e?.message || 'Failed to mark project as defunct');
        } finally {
            setUpdateStatusBusy(false);
        }
    }

    async function completeProject(project) {
        openUpdateStatusDialog(project);
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(f => {
            if (name === 'funder') return { ...f, funder: value, funder_poc: '' };
            if (name === 'last_issued_report_type') {
                const nextType = String(value || '').trim().toLowerCase();
                if (nextType !== 'interim') {
                    return { ...f, last_issued_report_type: value, interim_report_number: '' };
                }
                return { ...f, last_issued_report_type: value };
            }
            return { ...f, [name]: value };
        });
        if (name === 'last_issued_report_type') {
            setInProgressReportFile(null);
            setInProgressReportFileError('');
        }
    }

    function resetInstructionModalState() {
        setForm({
            project_name: '',
            reference: '',
            site_address: '',
            borrower_company: '',
            instruction_date: '',
            initial_report_fee: '',
            interim_report_fee: '',
            funder: '',
            funder_poc: '',
            last_issued_report_type: '',
            interim_report_number: ''
        });
        setInstructionFile(null);
        setInstructionFileError('');
        setInProgressReportFile(null);
        setInProgressReportFileError('');
        setIssuedFeeProposalsError('');
        setShowInstructionFormFields(false);
        setInstructionFormMode('standard');
        setSelectedFeeProposal(null);
    }

    async function loadIssuedFeeProposals() {
        setIssuedFeeProposalsLoading(true);
        setIssuedFeeProposalsError('');
        try {
            const response = await fetch(`${API_BASE_URL}/fee-proposals`, {
                method: 'GET',
                headers: { ...authHeaders() }
            });
            const json = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error((json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${response.status}`);
            }
            const rows = Array.isArray(json?.items) ? json.items : [];
            setIssuedFeeProposals(rows.filter((row) => String(row?.status || row?.status_label || '').trim().toLowerCase() === 'issued'));
        } catch (e) {
            setIssuedFeeProposals([]);
            setIssuedFeeProposalsError(e?.message || 'Failed to load issued fee proposals');
        } finally {
            setIssuedFeeProposalsLoading(false);
        }
    }

    function openNewInstructionDialog() {
        setShowProjectForm(true);
        resetInstructionModalState();
        void loadIssuedFeeProposals();
    }

    function closeNewInstructionDialog() {
        setShowProjectForm(false);
        resetInstructionModalState();
    }

    function startNewInstruction() {
        setShowInstructionFormFields(true);
        setInstructionFormMode('standard');
        setIssuedFeeProposalsError('');
        setSelectedFeeProposal(null);
    }

    function startInProgressInstruction() {
        setShowInstructionFormFields(true);
        setInstructionFormMode('inprogress');
        setIssuedFeeProposalsError('');
        setSelectedFeeProposal(null);
        setInstructionFile(null);
        setInstructionFileError('');
    }

    function startInstructionFromFeeProposal(proposal) {
        setForm(buildInstructionFormFromFeeProposal(proposal));
        setInstructionFile(null);
        setInstructionFileError('');
        setShowInstructionFormFields(true);
        setInstructionFormMode('standard');
        setIssuedFeeProposalsError('');
        setSelectedFeeProposal(proposal || null);
    }

    function authHeaders() {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    function setInstructionFileFromInput(file) {
        if (!file) {
            setInstructionFile(null);
            setInstructionFileError('');
            return;
        }
        const name = String(file.name || '').toLowerCase();
        const type = String(file.type || '').toLowerCase();
        const ok = type === 'application/pdf'
            || type === 'application/msword'
            || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            || name.endsWith('.pdf')
            || name.endsWith('.doc')
            || name.endsWith('.docx');
        if (!ok) {
            setInstructionFile(null);
            setInstructionFileError('Only PDF, DOC, or DOCX files are allowed.');
            return;
        }
        setInstructionFile(file);
        setInstructionFileError('');
    }

    function setInProgressReportFileFromInput(file) {
        if (!file) {
            setInProgressReportFile(null);
            setInProgressReportFileError('');
            return;
        }
        const name = String(file.name || '').toLowerCase();
        const type = String(file.type || '').toLowerCase();
        const ok = type === 'application/pdf' || name.endsWith('.pdf');
        if (!ok) {
            setInProgressReportFile(null);
            setInProgressReportFileError('Only PDF files are allowed for in-progress issued reports.');
            return;
        }
        setInProgressReportFile(file);
        setInProgressReportFileError('');
    }

    async function uploadInProgressIssuedReport(reference, file, reportType, interimNumber) {
        if (!reference || !file) return;
        const type = String(reportType || '').trim().toLowerCase();
        const fd = new FormData();
        fd.append('file', file);
        fd.append('report_type', type);
        if (type === 'interim' && String(interimNumber || '').trim()) {
            fd.append('interim_report_number', String(interimNumber || '').trim());
        }

        setInstructionUploading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/in-progress-issued-report`, {
                method: 'POST',
                headers: authHeaders(),
                body: fd
            });
            const json = await res.json().catch(() => null);
            if (!res.ok) {
                throw new Error((json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`);
            }
        } finally {
            setInstructionUploading(false);
        }
    }

    function setInProgressMetadata(reference, reportType, reportNumber) {
        const ref = String(reference || '').trim();
        if (!ref) return;
        const indicator = formatProgressIndicator(reportType, reportNumber);
        setInstructionProgressMetaByRef((prev) => ({
            ...prev,
            [ref]: {
                indicator,
                report_type: String(reportType || '').trim(),
                report_number: String(reportNumber || '').trim(),
                first_action_required: 'Project Setup'
            }
        }));
    }

    function getInProgressMetadata(reference) {
        const ref = String(reference || '').trim();
        if (!ref) return null;
        const row = instructionProgressMetaByRef?.[ref];
        return row && typeof row === 'object' ? row : null;
    }

    async function uploadInstructionDocument(reference, file) {
        if (!reference || !file) return;
        const fd = new FormData();
        fd.append('file', file);
        setInstructionUploading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/instruction-documents`, {
                method: 'POST',
                headers: authHeaders(),
                body: fd
            });
            const json = await res.json().catch(() => null);
            if (!res.ok) {
                throw new Error((json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`);
            }
        } finally {
            setInstructionUploading(false);
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!form.project_name || !form.reference) {
            setMessage('Project Name and Project Reference are required');
            return;
        }

        const isInProgress = instructionFormMode === 'inprogress';
        const reportType = String(form.last_issued_report_type || '').trim().toLowerCase();
        const reportNumber = String(form.interim_report_number || '').trim();

        if (isInProgress && !reportType) {
            setMessage('Last Issued Report Type is required for in-progress instructions');
            return;
        }
        if (isInProgress && reportType === 'interim' && !reportNumber) {
            setMessage('Interim Report Number is required when Last Issued Report Type is Interim');
            return;
        }

        const newProject = {
            project_name: form.project_name,
            reference: form.reference,
            site_address: form.site_address,
            borrower_company: form.borrower_company,
            instruction_date: form.instruction_date || toUkIsoDateOnly(new Date()),
            initial_report_fee: form.initial_report_fee,
            interim_report_fee: form.interim_report_fee,
            funder: form.funder,
            funder_poc: form.funder_poc,
            fee_proposal_id: String(selectedFeeProposal?.fee_proposal_id || '').trim() || undefined
        };

        try {
            const res = await fetch(`${API_BASE_URL}/key_project_data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(newProject)
            });
            const json = await res.json().catch(async () => {
                const txt = await res.text().catch(() => '');
                return { error: txt || `HTTP ${res.status}` };
            });
            if (!res.ok) {
                throw new Error((json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`);
            }

            if (!json || typeof json !== 'object') throw new Error('Unexpected response from server');
            const normalized = normalizeProjectDates(json);
            if (!normalized?.reference && !newProject.reference) throw new Error('Missing project reference');

            const ref = normalized?.reference || newProject.reference;
            const mergedProject = isInProgress
                ? {
                    ...normalized,
                    last_issued_report_type: reportType,
                    interim_report_number: reportNumber,
                    first_action_required: 'Project Setup'
                }
                : normalized;

            setProjects((list) => [...list, mergedProject]);

            if (isInProgress) {
                setInProgressMetadata(ref, reportType, reportNumber);
                try {
                    await fetch(`${API_BASE_URL}/key_project_data/reference/${encodeURIComponent(ref)}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', ...authHeaders() },
                        body: JSON.stringify({
                            status: 'in_progress',
                            status_updated_at: new Date().toISOString(),
                            in_progress_report_type: reportType,
                            in_progress_interim_number: reportType === 'interim' ? Number(reportNumber || 0) || null : null,
                            first_action_required: 'Project Setup'
                        })
                    });
                } catch {
                    // Non-blocking: core create succeeded, this marker can be retried later.
                }
                if (inProgressReportFile) {
                    try {
                        await uploadInProgressIssuedReport(ref, inProgressReportFile, reportType, reportNumber);
                    } catch (err) {
                        setMessage(err?.message || 'In-progress report upload failed');
                    }
                }
            } else if (instructionFile) {
                try {
                    await uploadInstructionDocument(ref, instructionFile);
                } catch (err) {
                    setMessage(err?.message || 'Instruction document upload failed');
                }
            }

            closeNewInstructionDialog();
            setMessage('Project created');
            setTimeout(() => setMessage(''), 2500);
        } catch (err) {
            setMessage(err?.message || 'Failed to add project');
        }
    }

    function startEdit(project) {
        setEditingId(project.reference);
        setEditForm({
            project_name: project.project_name || '',
            reference: project.reference || '',
            site_address: project.site_address || '',
            instruction_date: toUkIsoDateOnly(project.instruction_date),
            initial_report_fee: project.initial_report_fee ?? '',
            interim_report_fee: project.interim_report_fee ?? '',
            funder: project.funder || '',
            funder_poc: project.funder_poc || ''
        });
    }

    function cancelEdit() {
        setEditingId(null);
        setEditForm({});
    }

    async function saveEdit(e) {
        if (e?.preventDefault) e.preventDefault();
        if (!editForm.project_name || !editingId) { window.alert('Project Name and Project Reference are required'); return; }

        const previous = projects.find(p => p.reference === editingId);
        if (!previous) { cancelEdit(); return; }

        try {
            setMessage('');
            const updated = await updateProject(previous, editForm);
            // backend might return updated row or {data: row}
            const row = normalizeProjectDates((updated && updated.data) ? updated.data : updated);
            setProjects(list => list.map(p => p.reference === previous.reference ? { ...p, ...row } : p));
            setMessage('Project updated');
            setTimeout(() => setMessage(''), 2500);
            cancelEdit();
        } catch (err) {
            setMessage(err?.message || 'Failed to update project');
        }
    }

    return (
        <Box
            className="project-setup"
            sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                pb: 10,
                background: `radial-gradient(circle at top left, ${alpha('#60a5fa', 0.08)}, transparent 30%), radial-gradient(circle at top right, ${alpha('#14b8a6', 0.08)}, transparent 32%)`
            }}
        >
            <AnalyticsPageHero
                eyebrow="Instructions"
                title="Instructions and delivery control"
                actions={(
                    <Button variant="contained" size="large" onClick={openNewInstructionDialog} sx={{ minWidth: 190 }}>
                        New Instruction
                    </Button>
                )}
            />

            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                }}
            >
                <AnalyticsMetricCard
                    icon={<DescriptionRoundedIcon fontSize="small" />}
                    label="Active Queue"
                    value={existingProjects.length}
                    caption="Confirmed instructions currently in delivery and awaiting action or progression."
                    accent="linear-gradient(135deg, #0f172a 0%, #2563eb 100%)"
                />
                <AnalyticsMetricCard
                    icon={<AssignmentTurnedInRoundedIcon fontSize="small" />}
                    label="Completed"
                    value={completedProjects.length}
                    caption="Instructions already issued with a final report date and retained in the archive."
                    accent="linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)"
                />
                <AnalyticsMetricCard
                    icon={<PersonSearchRoundedIcon fontSize="small" />}
                    label="Pending Assignment"
                    value={activeUnassignedCount}
                    caption="Active instructions that still need a lead surveyor confirmed."
                    accent="linear-gradient(135deg, #b45309 0%, #f59e0b 100%)"
                />
                <AnalyticsMetricCard
                    icon={<GroupRoundedIcon fontSize="small" />}
                    label="Fee Exposure"
                    value={formatMoney(activeFeeValue)}
                    caption="Combined initial and interim fee values across the active instruction queue."
                    accent="linear-gradient(135deg, #4338ca 0%, #7c3aed 100%)"
                />
            </Box>

            {showProjectForm && (
                <Dialog
                    open
                    onClose={closeNewInstructionDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 4,
                            background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f8fbff', 0.96)} 100%)`
                        }
                    }}
                >
                    <DialogTitle>New Instruction</DialogTitle>
                    <DialogContent dividers>
                        {!showInstructionFormFields ? (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                                        Start from an issued fee proposal
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Select an issued fee proposal to prepopulate the instruction form, or choose New Instruction / Add In-Progress Instruction.
                                    </Typography>
                                </Box>

                                {issuedFeeProposalsError ? <Alert severity="error">{issuedFeeProposalsError}</Alert> : null}

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800 }}>Fee Proposal Reference No.</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Client</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Project Address</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {issuedFeeProposals.length ? issuedFeeProposals.map((proposal) => (
                                                <TableRow
                                                    key={proposal.fee_proposal_id || proposal.proposal_reference}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => startInstructionFromFeeProposal(proposal)}
                                                >
                                                    <TableCell sx={{ fontWeight: 700 }}>{proposal.proposal_reference || '—'}</TableCell>
                                                    <TableCell>{proposal.client_name || '—'}</TableCell>
                                                    <TableCell>{proposal.project_address || '—'}</TableCell>
                                                    <TableCell>{proposal.status_label || proposal.status || '—'}</TableCell>
                                                </TableRow>
                                            )) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                                                        {issuedFeeProposalsLoading ? 'Loading issued fee proposals…' : 'No issued fee proposals are available.'}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Button onClick={() => void loadIssuedFeeProposals()} variant="outlined" disabled={issuedFeeProposalsLoading}>
                                        {issuedFeeProposalsLoading ? 'Refreshing…' : 'Refresh list'}
                                    </Button>
                                    <Stack direction="row" spacing={1}>
                                        <Button onClick={startInProgressInstruction} variant="outlined">
                                            Add In-Progress Instruction
                                        </Button>
                                        <Button onClick={startNewInstruction} variant="contained">
                                            New Instruction
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        ) : (
                            <Stack component="form" id="project-add-form" onSubmit={handleSubmit} spacing={2} sx={{ mt: 1 }}>
                                {instructionFormMode !== 'inprogress' ? (
                                    <Box>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Instruction Document</Typography>
                                        <Box
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const file = e.dataTransfer?.files?.[0];
                                                setInstructionFileFromInput(file);
                                            }}
                                            sx={{
                                                border: '2px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                p: 2,
                                                textAlign: 'center',
                                                bgcolor: 'background.default',
                                                color: 'text.secondary'
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 600 }}>Drag & drop instruction document here</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>PDF/DOC/DOCX up to 50MB</Typography>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={(e) => setInstructionFileFromInput(e.target.files?.[0])}
                                                style={{ marginTop: 10 }}
                                            />
                                            {instructionFile ? (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.primary' }}>
                                                    Selected: <strong>{instructionFile.name}</strong>
                                                </Typography>
                                            ) : null}
                                            {instructionFileError ? (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
                                                    {instructionFileError}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                ) : null}
                                <TextField label="Project Name" name="project_name" value={form.project_name} onChange={handleChange} fullWidth />
                                <TextField label="Project Reference" name="reference" value={form.reference} onChange={handleChange} fullWidth />
                                <TextField label="Site Address" name="site_address" value={form.site_address} onChange={handleChange} fullWidth />
                                <TextField label="Borrower Company" name="borrower_company" value={form.borrower_company} onChange={handleChange} fullWidth />
                                <TextField type="date" label="Instruction Date" name="instruction_date" value={form.instruction_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField label="Initial Report Fee (£)" type="number" inputProps={{ step: '0.01', min: 0 }} name="initial_report_fee" value={form.initial_report_fee} onChange={handleChange} fullWidth />
                                    <TextField label="Interim Report Fee (£)" type="number" inputProps={{ step: '0.01', min: 0 }} name="interim_report_fee" value={form.interim_report_fee} onChange={handleChange} fullWidth />
                                </Stack>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Funder</InputLabel>
                                        <Select label="Funder" name="funder" value={form.funder || ''} onChange={handleChange}>
                                            <MenuItem value="">-- Select Funder --</MenuItem>
                                            {clients.map(c => (
                                                <MenuItem key={c.id} value={c.companyName}>{c.companyName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel>Funder POC</InputLabel>
                                        <Select
                                            label="Funder POC"
                                            name="funder_poc"
                                            value={form.funder_poc || ''}
                                            onChange={handleChange}
                                            disabled={!getContactsForFunder(form.funder).length}
                                        >
                                            <MenuItem value="">
                                                {getContactsForFunder(form.funder).length ? '-- Select Contact --' : 'No contacts available'}
                                            </MenuItem>
                                            {getContactsForFunder(form.funder).map((c, idx) => (
                                                <MenuItem key={`funder_contact_${idx}`} value={c.name || ''}>
                                                    {c.name || 'Unnamed'}{c.email ? ` • ${c.email}` : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                {instructionFormMode === 'inprogress' ? (
                                    <>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Last Issued Report Type</InputLabel>
                                                <Select
                                                    label="Last Issued Report Type"
                                                    name="last_issued_report_type"
                                                    value={form.last_issued_report_type || ''}
                                                    onChange={handleChange}
                                                >
                                                    <MenuItem value="">-- Select Report Type --</MenuItem>
                                                    <MenuItem value="initial">Initial</MenuItem>
                                                    <MenuItem value="interim">Interim</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {String(form.last_issued_report_type || '').toLowerCase() === 'interim' ? (
                                                <TextField
                                                    label="Interim Report Number"
                                                    name="interim_report_number"
                                                    value={form.interim_report_number}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    required
                                                />
                                            ) : null}
                                        </Stack>

                                        {String(form.last_issued_report_type || '').trim() ? (
                                            <Box>
                                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                                                    Upload {formatProgressIndicator(form.last_issued_report_type, form.interim_report_number) || 'Report'}
                                                </Typography>
                                                <Box
                                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const file = e.dataTransfer?.files?.[0];
                                                        setInProgressReportFileFromInput(file);
                                                    }}
                                                    sx={{
                                                        border: '2px dashed',
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        p: 2,
                                                        textAlign: 'center',
                                                        bgcolor: 'background.default',
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600 }}>Drag & drop report file here</Typography>
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>PDF up to 50MB</Typography>
                                                    <input
                                                        type="file"
                                                        accept=".pdf,application/pdf"
                                                        onChange={(e) => setInProgressReportFileFromInput(e.target.files?.[0])}
                                                        style={{ marginTop: 10 }}
                                                    />
                                                    {inProgressReportFile ? (
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.primary' }}>
                                                            Selected: <strong>{inProgressReportFile.name}</strong>
                                                        </Typography>
                                                    ) : null}
                                                    {inProgressReportFileError ? (
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
                                                            {inProgressReportFileError}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Box>
                                        ) : null}
                                    </>
                                ) : null}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeNewInstructionDialog} variant="text">Cancel</Button>
                        {showInstructionFormFields ? (
                            <Button type="submit" form="project-add-form" variant="contained" disabled={instructionUploading}>
                                {instructionUploading ? 'Uploading…' : 'Create Project'}
                            </Button>
                        ) : null}
                        {message ? <Typography variant="caption" color="success.main" sx={{ ml: 2 }}>{message}</Typography> : null}
                    </DialogActions>
                </Dialog>
            )}
            <AnalyticsPanelSection
                eyebrow="Live delivery"
                title="Confirmed Instructions"
                right={(
                    <TextField
                        value={existingSearch}
                        onChange={(e) => setExistingSearch(e.target.value)}
                        placeholder="Search by reference, name, address…"
                        size="small"
                        sx={{ minWidth: { xs: '100%', sm: 280 }, maxWidth: 360, width: '100%' }}
                    />
                )}
            >
                {existingProjects.length === 0 ? (
                    <Box sx={{ ...glassSubCardSx, textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                            No active instructions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            New instructions will appear here as soon as they are confirmed.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {existingProjects.map((project) => (
                            <Paper
                                key={project.id || project.reference}
                                sx={{
                                    ...analyticsPanelSx,
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    boxShadow: expandedRef === project.reference
                                        ? '0 24px 56px rgba(15, 23, 42, 0.14)'
                                        : analyticsPanelSx.boxShadow
                                }}
                            >
                                <ButtonBase
                                    onClick={() => setExpandedRef((cur) => (cur === project.reference ? null : project.reference))}
                                    sx={{
                                        width: '100%',
                                        textAlign: 'left',
                                        display: 'block',
                                        background: expandedRef === project.reference
                                            ? `linear-gradient(135deg, ${alpha('#0f172a', 0.96)} 0%, ${alpha('#2563eb', 0.92)} 100%)`
                                            : `linear-gradient(135deg, ${alpha('#0f172a', 0.9)} 0%, ${alpha('#1d4ed8', 0.82)} 100%)`,
                                        color: '#fff'
                                    }}
                                    aria-expanded={expandedRef === project.reference}
                                >
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ width: '100%', p: { xs: 2, sm: 2.4 } }}>
                                        <Stack spacing={1}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                                <Chip label={project.reference || '—'} sx={{ fontWeight: 800, bgcolor: alpha('#ffffff', 0.18), color: '#fff' }} />
                                                {getInProgressMetadata(project.reference)?.indicator ? (
                                                    <Chip
                                                        label={getInProgressMetadata(project.reference)?.indicator}
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: alpha('#38bdf8', 0.22),
                                                            color: '#fff',
                                                            border: `1px solid ${alpha('#7dd3fc', 0.42)}`
                                                        }}
                                                    />
                                                ) : null}
                                                <Chip
                                                    label={project.lead_surveyor ? `Assigned: ${project.lead_surveyor}` : 'Lead surveyor pending'}
                                                    sx={{
                                                        fontWeight: 700,
                                                        bgcolor: project.lead_surveyor ? alpha('#14b8a6', 0.2) : alpha('#f59e0b', 0.2),
                                                        color: '#fff',
                                                        border: `1px solid ${project.lead_surveyor ? alpha('#5eead4', 0.35) : alpha('#fcd34d', 0.35)}`
                                                    }}
                                                />
                                            </Stack>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: '-0.03em' }}>
                                                    {project.project_name || 'Untitled Project'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.82), maxWidth: 760 }}>
                                                    {project.site_address || 'No site address recorded'}
                                                </Typography>
                                                {getInProgressMetadata(project.reference)?.first_action_required ? (
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: alpha('#ffffff', 0.8) }}>
                                                        First action required: {getInProgressMetadata(project.reference)?.first_action_required}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ color: '#fff' }}>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" sx={{ display: 'block', color: alpha('#ffffff', 0.74), textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                    Instruction Date
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {formatDisplayDate(project.instruction_date)}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: '50%',
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    backgroundColor: alpha('#ffffff', 0.14),
                                                    transform: expandedRef === project.reference ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 150ms ease'
                                                }}
                                            >
                                                <ExpandMoreIcon />
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </ButtonBase>

                                <Collapse in={expandedRef === project.reference} timeout="auto" unmountOnExit>
                                    <Divider />
                                    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                                        <Stack spacing={2.25}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1.25,
                                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                                                }}
                                            >
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Site Address
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {project.site_address || '—'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Funder
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {project.funder || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {project.funder_poc ? `POC: ${project.funder_poc}` : 'No point of contact selected'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Fees
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        Initial {formatMoney(project.initial_report_fee)}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        Interim {formatMoney(project.interim_report_fee)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Programme Duration
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {durationLoadingByRef[project.reference] ? 'Loading…' : `${getDurationDisplay(project)} months`}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {(() => {
                                                const contacts = getContactsForFunder(project.funder);
                                                if (!contacts.length) return null;
                                                return (
                                                    <Box sx={{ ...glassSubCardSx, p: 1.6 }}>
                                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                            Funder Contacts
                                                        </Typography>
                                                        <Stack spacing={0.6} sx={{ mt: 0.5 }}>
                                                            {contacts.map((c, idx) => (
                                                                <Typography key={`proj_contact_${project.reference}_${idx}`} variant="body2">
                                                                    {c.name || '—'}{c.phone ? ` • ${c.phone}` : ''}{c.email ? ` • ${c.email}` : ''}
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })()}

                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1.5,
                                                    gridTemplateColumns: { xs: '1fr', xl: 'minmax(280px, 360px) minmax(0, 1fr)' }
                                                }}
                                            >
                                                <Box sx={{ ...glassSubCardSx, p: 1.6 }}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Lead Surveyor Assignment
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25 }}>
                                                        Manual assignment remains available here and writes back to the same project record.
                                                    </Typography>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Choose Surveyor to Assign</InputLabel>
                                                        <Select
                                                            label="Choose Surveyor to Assign"
                                                            value={project.lead_surveyor || ''}
                                                            onChange={async (e) => {
                                                                const nextName = String(e.target.value || '').trim();
                                                                try {
                                                                    await updateProject(project, { ...project, lead_surveyor: nextName });
                                                                    setProjects((list) => (Array.isArray(list)
                                                                        ? list.map((p) => (p.reference === project.reference ? { ...p, lead_surveyor: nextName } : p))
                                                                        : list));
                                                                } catch (err) {
                                                                    setMessage(err?.message || 'Failed to update lead surveyor');
                                                                    setTimeout(() => setMessage(''), 2500);
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem value="">Unassigned</MenuItem>
                                                            {assignableSurveyors.map((member) => (
                                                                <MenuItem key={member.id} value={member.name}>
                                                                    {member.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box>

                                                <Box sx={{ ...glassSubCardSx, p: 0.75 }}>
                                                    <RecommendedSurveyorsPanel
                                                        projectReference={project.reference}
                                                        currentLeadSurveyor={project.lead_surveyor || ''}
                                                        allowedNames={assignableSurveyors.map((member) => member.name)}
                                                        onAssigned={(out) => {
                                                            const assignedName = String(out?.surveyor?.name || '').trim();
                                                            if (!assignedName) return;
                                                            setProjects((list) => (Array.isArray(list)
                                                                ? list.map((p) => (p.reference === project.reference ? { ...p, lead_surveyor: assignedName } : p))
                                                                : list));
                                                        }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                                <Button variant="contained" onClick={(e) => { e.stopPropagation(); startEdit(project); }}>
                                                    Edit
                                                </Button>
                                                <Button variant="contained" color="success" onClick={async (e) => {
                                                    e.stopPropagation();
                                                    await completeProject(project);
                                                }}>
                                                    Update
                                                </Button>
                                                <Button variant="contained" color="error" onClick={async (e) => {
                                                    e.stopPropagation();
                                                    openDeleteDialog(project.reference, { isCompleted: false });
                                                }}>
                                                    Delete
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </AnalyticsPanelSection>

            <AnalyticsPanelSection
                eyebrow="Archive"
                title="Archived Instructions"
                subtitle="Closed work remains available for audit, reference, and controlled deletion."
            >
                {completedProjects.length === 0 ? (
                    <Box sx={{ ...glassSubCardSx, textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                            No archived instructions yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Completed and defunct instructions will move here once archived.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {completedProjects.map((project) => (
                            <Paper key={(project.id || project.reference) + '_completed'} sx={{ ...analyticsPanelSx, borderRadius: 4, overflow: 'hidden' }}>
                                <ButtonBase
                                    onClick={() => setExpandedRef((cur) => (cur === project.reference ? null : project.reference))}
                                    sx={{
                                        width: '100%',
                                        textAlign: 'left',
                                        display: 'block',
                                        background: `linear-gradient(135deg, ${alpha('#0f766e', 0.92)} 0%, ${alpha('#0f172a', 0.92)} 100%)`,
                                        color: '#fff'
                                    }}
                                    aria-expanded={expandedRef === project.reference}
                                >
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ width: '100%', p: { xs: 2, sm: 2.4 } }}>
                                        <Stack spacing={1}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                                <Chip label={project.reference || '—'} sx={{ fontWeight: 800, bgcolor: alpha('#ffffff', 0.18), color: '#fff' }} />
                                                {getArchiveStatus(project.reference, project.status) === 'defunct' ? (
                                                    <Chip label="Defunct" sx={{ fontWeight: 800, bgcolor: alpha('#f59e0b', 0.22), color: '#fff', border: `1px solid ${alpha('#fcd34d', 0.45)}` }} />
                                                ) : (
                                                    <Chip label="Completed" sx={{ fontWeight: 800, bgcolor: alpha('#14b8a6', 0.2), color: '#fff', border: `1px solid ${alpha('#99f6e4', 0.3)}` }} />
                                                )}
                                            </Stack>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: '-0.03em' }}>
                                                    {project.project_name || 'Untitled Project'}
                                                </Typography>
                                                {getArchiveStatus(project.reference, project.status) === 'defunct' ? (
                                                    <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.82) }}>
                                                        Status: Defunct
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.82) }}>
                                                        Final report issued: {completedByRef[project.reference] || '—'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Box
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: '50%',
                                                display: 'grid',
                                                placeItems: 'center',
                                                backgroundColor: alpha('#ffffff', 0.14),
                                                transform: expandedRef === project.reference ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 150ms ease'
                                            }}
                                        >
                                            <ExpandMoreIcon />
                                        </Box>
                                    </Stack>
                                </ButtonBase>

                                <Collapse in={expandedRef === project.reference} timeout="auto" unmountOnExit>
                                    <Divider />
                                    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                                        <Stack spacing={2.25}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1.25,
                                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                                                }}
                                            >
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Final Report Issue Date
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {completedByRef[project.reference] || '—'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Site Address
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {project.site_address || '—'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Funder
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {project.funder || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {project.funder_poc ? `POC: ${project.funder_poc}` : 'No point of contact selected'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={glassSubCardSx}>
                                                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                        Duration
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {durationLoadingByRef[project.reference] ? 'Loading…' : `${getDurationDisplay(project)} months`}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {(() => {
                                                const contacts = getContactsForFunder(project.funder);
                                                if (!contacts.length) return null;
                                                return (
                                                    <Box sx={{ ...glassSubCardSx, p: 1.6 }}>
                                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                            Funder Contacts
                                                        </Typography>
                                                        <Stack spacing={0.6} sx={{ mt: 0.5 }}>
                                                            {contacts.map((c, idx) => (
                                                                <Typography key={`proj_contact_done_${project.reference}_${idx}`} variant="body2">
                                                                    {c.name || '—'}{c.phone ? ` • ${c.phone}` : ''}{c.email ? ` • ${c.email}` : ''}
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })()}

                                            <Box sx={{ ...glassSubCardSx, p: 1.6 }}>
                                                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                                    Commercials and Ownership
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    Initial {formatMoney(project.initial_report_fee)} • Interim {formatMoney(project.interim_report_fee)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Lead surveyor: {project.lead_surveyor || '—'} • Instruction date: {formatDisplayDate(project.instruction_date)}
                                                </Typography>
                                            </Box>

                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                                <Button variant="contained" color="error" onClick={async (e) => {
                                                    e.stopPropagation();
                                                    openDeleteDialog(project.reference, { isCompleted: true });
                                                }}>
                                                    Delete
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </AnalyticsPanelSection>

            {editingId && (
                <Dialog
                    open
                    onClose={cancelEdit}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 4,
                            background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f8fbff', 0.96)} 100%)`
                        }
                    }}
                >
                    <DialogTitle>Edit Project</DialogTitle>
                    <DialogContent dividers>
                        <Stack component="form" id="project-edit-form" onSubmit={saveEdit} spacing={2} sx={{ mt: 1 }}>
                            <TextField label="Project Name" name="project_name" value={editForm.project_name || ''} onChange={(e) => setEditForm(f => ({ ...f, project_name: e.target.value }))} fullWidth />
                            <TextField label="Project Reference" name="reference" value={editForm.reference || ''} fullWidth disabled />
                            <TextField label="Site Address" name="site_address" value={editForm.site_address || ''} onChange={(e) => setEditForm(f => ({ ...f, site_address: e.target.value }))} fullWidth />
                            <TextField type="date" label="Instruction Date" name="instruction_date" value={editForm.instruction_date || ''} onChange={(e) => setEditForm(f => ({ ...f, instruction_date: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField label="Initial Report Fee (£)" type="number" inputProps={{ step: '0.01', min: 0 }} name="initial_report_fee" value={editForm.initial_report_fee ?? ''} onChange={(e) => setEditForm(f => ({ ...f, initial_report_fee: e.target.value }))} fullWidth />
                                <TextField label="Interim Report Fee (£)" type="number" inputProps={{ step: '0.01', min: 0 }} name="interim_report_fee" value={editForm.interim_report_fee ?? ''} onChange={(e) => setEditForm(f => ({ ...f, interim_report_fee: e.target.value }))} fullWidth />
                            </Stack>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Funder</InputLabel>
                                    <Select label="Funder" name="funder" value={editForm.funder || ''} onChange={(e) => setEditForm(f => ({ ...f, funder: e.target.value }))}>
                                        <MenuItem value="">-- Select Funder --</MenuItem>
                                        {clients.map(c => (
                                            <MenuItem key={c.id} value={c.companyName}>{c.companyName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>Funder POC</InputLabel>
                                    <Select
                                        label="Funder POC"
                                        name="funder_poc"
                                        value={editForm.funder_poc || ''}
                                        onChange={(e) => setEditForm(f => ({ ...f, funder_poc: e.target.value }))}
                                        disabled={!getContactsForFunder(editForm.funder).length}
                                    >
                                        <MenuItem value="">
                                            {getContactsForFunder(editForm.funder).length ? '-- Select Contact --' : 'No contacts available'}
                                        </MenuItem>
                                        {getContactsForFunder(editForm.funder).map((c, idx) => (
                                            <MenuItem key={`edit_funder_contact_${idx}`} value={c.name || ''}>
                                                {c.name || 'Unnamed'}{c.email ? ` • ${c.email}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelEdit} variant="text">Cancel</Button>
                        {message ? <Typography variant="caption" color={message === 'Project updated' ? 'success.main' : 'error.main'} sx={{ mr: 'auto' }}>{message}</Typography> : null}
                        <Button type="button" onClick={() => { void saveEdit(); }} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#fff8f8', 0.96)} 100%)`
                    }
                }}
            >
                <DialogTitle>Delete Project</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        This will permanently delete all SQL data for <strong>{deleteTargetRef || 'this project'}</strong> and purge its stored files.
                    </Typography>
                    <TextField
                        label="Deletion password"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        fullWidth
                        autoFocus
                        disabled={deleteBusy}
                        error={Boolean(deleteError)}
                        helperText={deleteError || 'Enter the admin deletion password to confirm.'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteBusy}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmDeleteProject} disabled={deleteBusy || !String(deletePassword || '').trim()}>
                        {deleteBusy ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={updateStatusDialogOpen}
                onClose={closeUpdateStatusDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f3fbf9', 0.96)} 100%)`
                    }
                }}
            >
                <DialogTitle>Update job status</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Select the update outcome for this instruction.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                        Complete: The project is complete and the final report has been issued.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Defunct: The project has not achieved funding and/or has been dropped by the client.
                    </Typography>
                    {updateStatusError ? (
                        <Alert severity="error" sx={{ mt: 1.5 }}>{updateStatusError}</Alert>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeUpdateStatusDialog} disabled={updateStatusBusy}>Cancel</Button>
                    <Button variant="contained" onClick={startCompleteProjectFlow} disabled={updateStatusBusy}>
                        Complete
                    </Button>
                    <Button variant="contained" color="warning" onClick={markProjectDefunct} disabled={updateStatusBusy}>
                        {updateStatusBusy ? 'Saving…' : 'Defunct'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={finalReportDialogOpen}
                onClose={closeFinalReportDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f3fbf9', 0.96)} 100%)`
                    }
                }}
            >
                <DialogTitle>Complete project</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Enter the date the final report was issued (YYYY-MM-DD).
                    </Typography>
                    <TextField
                        label="Final report issue date"
                        value={finalReportIssueDate}
                        onChange={(e) => setFinalReportIssueDate(e.target.value)}
                        fullWidth
                        autoFocus
                        disabled={finalReportBusy}
                        error={Boolean(finalReportError)}
                        helperText={finalReportError || 'Example: 2026-02-25'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFinalReportDialog} disabled={finalReportBusy}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={confirmFinalReportDialog}
                        disabled={finalReportBusy}
                    >
                        {finalReportBusy ? 'Saving…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ProjectSetup;
